/**
 * @file inward-image-viewer.tsx
 * @module app/modules/inward/components/inward-image-viewer
 * @description Pop-up viewer for GRN photos and documents (any file type): images zoom in /
 *   out / reset, PDFs are shown inline, other files get a card with Open / Download; previous /
 *   next (carousel) when there is more than one image. Zoom resets when changing image.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import ImageWithFallback from "../../_common/components/image-with-fallback";

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 4;
export const ZOOM_STEP = 0.5;

/** File name from a URL, without the query string. */
export const fileName = (url: string): string => {
  const path = String(url ?? "").split(/[?#]/)[0];
  return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1));
};

/** Upper-case extension ("PDF"), or "" when there is none. */
export const fileExtension = (url: string): string => {
  const name = fileName(url);
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : "";
};

/** How a file is shown: images zoomable, PDFs embedded, anything else as a file card. */
export const fileKind = (url: string): "image" | "pdf" | "file" => {
  const ext = fileExtension(url);
  if (
    [
      "JPG",
      "JPEG",
      "PNG",
      "WEBP",
      "GIF",
      "BMP",
      "HEIC",
      "HEIF",
      "SVG",
    ].includes(ext)
  )
    return "image";
  return ext === "PDF" ? "pdf" : "file";
};

interface InwardImageViewerProps {
  images: string[];
  /** index of the image to open, or null when closed */
  openIndex: number | null;
  onClose: () => void;
  title?: string;
}

const InwardImageViewer = ({
  images,
  openIndex,
  onClose,
  title,
}: InwardImageViewerProps) => {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(ZOOM_MIN);

  useEffect(() => {
    if (openIndex !== null) {
      setIndex(openIndex);
      setZoom(ZOOM_MIN);
    }
  }, [openIndex]);

  const go = (step: number) => {
    setIndex((i) => (i + step + images.length) % images.length);
    setZoom(ZOOM_MIN);
  };

  const multiple = images.length > 1;
  const kind = fileKind(images[index] ?? "");

  return (
    <Modal
      show={openIndex !== null && images.length > 0}
      onHide={onClose}
      size="xl"
      centered
      data-testid="image-viewer"
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-18">
          {title || "Photo"}
          {multiple ? ` · ${index + 1} of ${images.length}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="inward-viewer-stage" data-testid="image-viewer-stage">
          {kind === "image" ? (
            <ImageWithFallback
              src={images[index]}
              alt={`${title || "Photo"} ${index + 1}`}
              className="inward-viewer-img"
              style={{ transform: `scale(${zoom})` }}
              data-testid="image-viewer-img"
              noDefaultSize
            />
          ) : kind === "pdf" ? (
            <iframe
              src={images[index]}
              title={`${title || "Document"} ${index + 1}`}
              className="inward-viewer-pdf"
              data-testid="image-viewer-pdf"
            />
          ) : (
            <div className="inward-viewer-file" data-testid="image-viewer-file">
              <div className="inward-viewer-file-ext">
                {fileExtension(images[index]) || "FILE"}
              </div>
              <div className="fs-15 text-break">{fileName(images[index])}</div>
              <div className="fs-14 text-muted">
                This file type cannot be shown here.
              </div>
            </div>
          )}
          {multiple && (
            <>
              <Button
                variant="light"
                className="inward-viewer-nav inward-viewer-prev"
                aria-label="Previous image"
                onClick={() => go(-1)}
                data-testid="image-viewer-prev"
              >
                ‹
              </Button>
              <Button
                variant="light"
                className="inward-viewer-nav inward-viewer-next"
                aria-label="Next image"
                onClick={() => go(1)}
                data-testid="image-viewer-next"
              >
                ›
              </Button>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div className="d-flex gap-2">
          {multiple &&
            images.map((src, i) => (
              <button
                key={src}
                type="button"
                className={`inward-viewer-thumb ${i === index ? "active" : ""}`}
                onClick={() => {
                  setIndex(i);
                  setZoom(ZOOM_MIN);
                }}
                aria-label={`Show image ${i + 1}`}
                data-testid="image-viewer-thumb"
              >
                {fileKind(src) === "image" ? (
                  <ImageWithFallback src={src} alt="" noDefaultSize />
                ) : (
                  <span className="inward-viewer-thumb-ext">
                    {fileExtension(src) || "FILE"}
                  </span>
                )}
              </button>
            ))}
        </div>
        <div className="d-flex align-items-center gap-2">
          <a
            href={images[index]}
            target="_blank"
            rel="noreferrer"
            download
            className="btn btn-outline-primary inward-touch-btn"
            data-testid="image-viewer-open"
          >
            Open / Download
          </a>
          {kind === "image" && (
            <>
              <Button
                variant="light"
                className="inward-touch-btn"
                disabled={zoom <= ZOOM_MIN}
                onClick={() =>
                  setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))
                }
                aria-label="Zoom out"
                data-testid="image-viewer-zoom-out"
              >
                −
              </Button>
              <span
                className="fs-15 fw-semibold"
                data-testid="image-viewer-zoom"
              >
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="light"
                className="inward-touch-btn"
                disabled={zoom >= ZOOM_MAX}
                onClick={() =>
                  setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))
                }
                aria-label="Zoom in"
                data-testid="image-viewer-zoom-in"
              >
                +
              </Button>
              <Button
                variant="light"
                className="inward-touch-btn"
                disabled={zoom === ZOOM_MIN}
                onClick={() => setZoom(ZOOM_MIN)}
                data-testid="image-viewer-reset"
              >
                Reset
              </Button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default InwardImageViewer;
